// useWtIoClient.ts
// `socket.io-client` is a sizeable dependency that's only needed for live-render
// editor preview sessions, so it's imported dynamically (see `_connect`) to keep
// it out of the initial bundle / off the main thread on normal page loads.
import type { Socket } from "socket.io-client";
import { computed, ComputedRef, onMounted, onUnmounted, ref, shallowRef } from "vue";

type TransportName = "webtransport" | "websocket" | "polling" | undefined;

export interface UseTransportOptions {
    url: string;                          // e.g. "https://api.example.com"
    autoConnect?: boolean;                // default: true
    reconnectionAttempts?: number;        // default: Infinity
    reconnectionDelayMax?: number;        // default: 8000
    withCredentials?: boolean;            // default: false (set true if using cookies)
    auth?: Record<string, unknown>;       // sent on connect
    extraHeaders?: Record<string, string>;// Node/Electron only
    transports?: ("webtransport" | "websocket" | "polling")[]; // default order below
    // SSR guard: if you're rendering on the server, set to false to avoid connecting
    enableOnMounted?: boolean;      // default: true
}

export interface UseTransport {
    // reactive state
    connected: ComputedRef<boolean>;
    connecting: ComputedRef<boolean>;
    transport: ComputedRef<TransportName>;
    id: ComputedRef<string | undefined>;
    lastError: ComputedRef<any>;

    // methods
    connect: () => void;
    disconnect: () => void;
    emit: (event: string, ...args: any[]) => void;
    on: (event: string, handler: (...args: any[]) => void) => () => void;
    join: (room: string, cb?: (ack: unknown) => void) => void;
    leave: (room: string, cb?: (ack: unknown) => void) => void;
}

/**
 * WebTransport-first Socket.IO composable for Vue 3
 * - prefers 'webtransport', then 'websocket', then 'polling'
 * - exposes reactive connection state
 * - event helpers + simple room helpers (requires server handlers)
 */
export function useTransport(opts: UseTransportOptions): UseTransport {
    const {
        url,
        autoConnect = true,
        reconnectionAttempts = Infinity,
        reconnectionDelayMax = 8000,
        withCredentials = false,
        auth,
        extraHeaders,
        transports = ["webtransport", "websocket", "polling"],
        enableOnMounted = true,
    } = opts;

    const socket = shallowRef<Socket | null>(null);
    const isConnected = ref(false);
    const isConnecting = ref(false);
    const _lastError = ref<unknown | null>(null);
    const pendingListeners = new Map<string, ((...args: any[]) => void)[]>();

    const _connect = async () => {

        if (socket.value?.connected || isConnecting.value) return;
        isConnecting.value = true;

        // Guard: client-only (avoid SSR crashes)
        if (typeof window === "undefined") return;

        // Load socket.io-client on demand so it isn't parsed on normal page loads.
        const { io } = await import("socket.io-client");

        const s = io(url, {
            transports,
            autoConnect,
            reconnectionAttempts,
            reconnectionDelayMax,
            withCredentials,
            auth,
            extraHeaders,
        });


        // lifecycle
        s.on("connect", () => {
            isConnected.value = true;
            isConnecting.value = false;
            _lastError.value = null;
        });

        s.on("disconnect", (reason) => {
            isConnected.value = false;
        });

        s.io.on("reconnect_attempt", (attempt) => {
            isConnecting.value = true;
        });

        s.on("connect_error", (err) => {
            _lastError.value = err;
            isConnecting.value = false;
        });

        socket.value = s;

        // Apply any pending listeners
        pendingListeners.forEach((handlers, event) => {
            handlers.forEach(handler => s.on(event, handler));
        });

        // Manually connect if this is called from connect()
        if (!autoConnect) {
            s.connect();
        }
    };

    const _disconnect = () => {
        if (socket.value) {
            socket.value.disconnect();
            socket.value = null;
            isConnected.value = false;
            isConnecting.value = false;
        }
    };

    onMounted(() => {
        if (enableOnMounted && autoConnect) _connect();
    });

    onUnmounted(() => {
        _disconnect();
    });

    // Public API
    const connect = () => _connect();
    const disconnect = () => _disconnect();

    const emit = (event: string, ...args: any[]) => {
        socket.value?.emit(event, ...args);
    };

    const on = (event: string, handler: (...args: any[]) => void) => {
        // If socket exists, add listener immediately
        if (socket.value) {
            socket.value.on(event, handler);
        } else {
            // Store listener to be applied when socket is created
            if (!pendingListeners.has(event)) {
                pendingListeners.set(event, []);
            }
            pendingListeners.get(event)!.push(handler);
        }

        // unsubscribe function
        return () => {
            socket.value?.off(event, handler);
            // Also remove from pending listeners
            const handlers = pendingListeners.get(event);
            if (handlers) {
                const index = handlers.indexOf(handler);
                if (index > -1) {
                    handlers.splice(index, 1);
                }
                if (handlers.length === 0) {
                    pendingListeners.delete(event);
                }
            }
        };
    };

    // simple room helpers (requires matching server handlers)
    const join = (room: string, cb?: (ack: unknown) => void) =>
        emit("room:join", { room }, cb);
    const leave = (room: string, cb?: (ack: unknown) => void) =>
        emit("room:leave", { room }, cb);

    const transport = computed<TransportName>(() => {
        const name = socket.value?.io?.engine?.transport?.name as TransportName;
        return name;
    });

    const id = computed(() => socket.value?.id);
    const connected = computed(() => isConnected.value);
    const connecting = computed(() => isConnecting.value);
    const lastError = computed(() => _lastError.value);

    return {
        connected,
        connecting,
        transport,
        id,
        lastError,
        connect,
        disconnect,
        emit,
        on,
        join,
        leave
    };
}
